import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AutoLogoutProvider } from './contexts/AutoLogoutContext';
import Navbar from './components/Layout/Navbar';
import Dashboard from './pages/Dashboard';
import DataEngineDashboard from './pages/DataEngineDashboard';
import Login from './pages/Login';
import AdminPanel from './pages/AdminPanel';
import CoauthorRequest from './pages/CoauthorRequest';
import PublicationDetail from './pages/PublicationDetail';
import About from './pages/About';
import SavicLabPublications from './pages/SavicLabPublications';
import PublishedDashboard from './pages/PublishedDashboard';
import InProgressDashboard from './pages/InProgressDashboard';
import TotalPapersDashboard from './pages/TotalPapersDashboard';
import ActiveUsersDashboard from './pages/ActiveUsersDashboard';
import BackupManagement from './pages/BackupManagement';
import DataSourceMapping from './pages/DataSourceMapping';
import MedicationMapping from './pages/MedicationMapping';
import logoImage from './images/image.png';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src={logoImage} 
            alt="Publication System Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Auth0 Callback Handler - waits for Auth0 to process the callback and redirects to dashboard
const Auth0CallbackHandler: React.FC = () => {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const navigate = useNavigate();

  // Wait for Auth0 to finish processing the callback, then redirect
  useEffect(() => {
    if (!isLoading) {
      if (error) {
        console.error('Auth0 callback error:', error);
        // Redirect to login on error
        navigate('/login', { replace: true });
      } else if (isAuthenticated) {
        // Successfully authenticated, redirect to dashboard immediately
        // The ProtectedRoute will handle any additional loading
        navigate('/', { replace: true });
      } else {
        // Not authenticated after callback completes, redirect to login
        // This shouldn't normally happen, but handle it gracefully
        console.warn('Auth0 callback completed but user not authenticated');
        navigate('/login', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, error, navigate]);

  // Show loading state while processing
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <img 
          src={logoImage} 
          alt="Publication System Logo" 
          className="h-16 w-auto mx-auto mb-4"
        />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Completing login...</p>
      </div>
    </div>
  );
};

// Public Route Component (redirect to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
};

// Main App Layout
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main>{children}</main>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
};

// App Routes
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/callback"
        element={<Auth0CallbackHandler />}
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DataEngineDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/publications/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PublicationDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminPanel />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/publications/:id/coauthor"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CoauthorRequest />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/about"
        element={
          <ProtectedRoute>
            <AppLayout>
              <About />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/publications"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/savic-lab-publications"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SavicLabPublications />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/published"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PublishedDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/in-progress"
        element={
          <ProtectedRoute>
            <AppLayout>
              <InProgressDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/total-papers"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TotalPapersDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/active-users"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ActiveUsersDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Data Mapping */}
      <Route
        path="/data-mapping/sources"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DataSourceMapping />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/data-mapping/medications"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MedicationMapping />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/backups"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BackupManagement />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch all route - redirect to data engine dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN || '';
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || '';
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE || '';

  if (!domain || !clientId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Configuration Error</h1>
          <p className="text-gray-600">Auth0 environment variables are not set.</p>
          <p className="text-sm text-gray-500 mt-2">Please check your .env file.</p>
        </div>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin + '/callback',
        audience: audience,
        scope: 'openid profile email',
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <AuthProvider>
        <AutoLogoutProvider>
          <Router>
            <AppRoutes />
          </Router>
        </AutoLogoutProvider>
      </AuthProvider>
    </Auth0Provider>
  );
};

export default App;
